import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportTickets, SupportTicket } from '../../crm-support-ticket/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface UrgentTicketsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const UrgentTickets: React.FC<UrgentTicketsProps> = ({ limit = 5, embedded, settings }) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { supportTickets, isLoading } = useSupportTickets();

  const items = React.useMemo(() => {
    const arr = Array.isArray(supportTickets) ? supportTickets : [];
    return arr
      .filter((t: SupportTicket) => {
        const statusDescr = t.statusDescr?.pts_descr?.toLowerCase() || '';
        const isClosed =
          statusDescr.includes('closed') ||
          statusDescr.includes('resolved') ||
          statusDescr.includes('completed');
        const isUrgent = (t.priority ?? 0) >= 3;
        return !isClosed && isUrgent;
      })
      .sort((a: SupportTicket, b: SupportTicket) => {
        const pa = a.priority ?? 0;
        const pb = b.priority ?? 0;
        if (pb !== pa) return pb - pa;
        return (
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        );
      })
      .slice(0, effectiveLimit);
  }, [supportTickets, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<SupportTicket>
      title="Urgent Tickets"
      viewAllPath="/support-ticket"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No urgent tickets"
      getItemKey={(t) => t.id}
      embedded={embedded}
      renderItem={(ticket) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/support-ticket/${ticket.id}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/support-ticket/${ticket.id}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {ticket.title}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {ticket.reference && `${ticket.reference} · `}
              {ticket.client?.clientName ?? ''}
              {ticket.priority != null && ` · Priority ${ticket.priority}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default UrgentTickets;
